import { Link, Navigate, Outlet, useParams, type RouteObject } from "react-router-dom";
import { Banner, LegalFooter, MigrationPauseBanner, Spinner, TopBar } from "./components";
import { useMigration } from "./api/hooks";
import { useFeatures } from "./api/features";
import { useSession } from "./api/session";
import { useTheme } from "./theme";
import { t } from "./i18n/t";
import type { MigrationState } from "./api/types";
import { routeForState } from "./screens/stageRoute";
import { ConnectionsScreen } from "./screens/operator/ConnectionsScreen";
import { LoginScreen } from "./screens/operator/LoginScreen";
import { MigrationsList } from "./screens/operator/MigrationsList";
import { RequireSession } from "./screens/operator/RequireSession";
import { SettingsScreen } from "./screens/operator/SettingsScreen";
import { XeroReturn } from "./screens/connect/XeroReturn";
import { HmrcReturn } from "./screens/connect/HmrcReturn";
import { ForgotPasswordScreen } from "./screens/auth/ForgotPasswordScreen";
import { LoginScreen as AuthLoginScreen } from "./screens/auth/LoginScreen";
import { RegisterScreen } from "./screens/auth/RegisterScreen";
import { ResetPasswordScreen } from "./screens/auth/ResetPasswordScreen";
import { VerifyEmailScreen } from "./screens/auth/VerifyEmailScreen";
import { AcceptInvitationScreen } from "./screens/practice/AcceptInvitationScreen";
import { MfaChallengeScreen } from "./screens/auth/MfaChallengeScreen";
import { MfaSetupScreen } from "./screens/auth/MfaSetupScreen";
import { SecurityScreen } from "./screens/account/SecurityScreen";
import { StepUpDialog } from "./screens/account/StepUpDialog";
import { LegalScreen } from "./screens/legal/LegalScreen";

/**
 * §B1.3's routes. `src/main.tsx` (task 06a) finds this module by `import.meta.glob("./router.tsx")` and uses the
 * `routes` export; the seam exists because §D0 gives the two files to two tasks.
 *
 * Feature areas register themselves the same way: any `src/screens/<area>/routes.tsx` that exports
 * `routes: RouteObject[]` is mounted under `RequireSession` → `RootLayout`. Tasks 07, 08 and 09 therefore never
 * edit this file. `screens/operator/routes.tsx` does not exist on purpose — the console is wired inline below, and
 * creating one would double-register it.
 */
const featureModules = import.meta.glob<{ routes: RouteObject[] }>("./screens/*/routes.tsx", { eager: true });

export const featureRoutes: RouteObject[] = Object.entries(featureModules)
  .sort(([a], [b]) => a.localeCompare(b))
  .flatMap(([, module]) => module.routes ?? []);

function RootLayout(): JSX.Element {
  const { session, logout } = useSession();
  // P5-web §1: practice routes 404 when `LUCIMIG_PRACTICE_ENABLED` is off, so the nav link is gated on the same
  // flag rather than always rendered and left to 404 on click. `useFeatures()` defaults to `practice_enabled:
  // false` before the boot fetch resolves and for any test with no `<FeaturesProvider>` in the tree, so the
  // gate fails safe.
  const { features } = useFeatures();
  const { effective, toggle } = useTheme();
  return (
    <>
      <TopBar
        operator={session?.operator}
        practiceEnabled={features.practice_enabled}
        securityLink={Boolean(session?.mfa)}
        theme={effective}
        onToggleTheme={toggle}
        onSignOut={() => void logout()}
      />
      {/* F3 (C1): one banner, every `/migrations/:id/*` screen, not just Status — see that component's own
          docstring for why a StatusScreen-only line would miss F3's headline (extracting) case. */}
      <MigrationPauseBanner />
      <Outlet />
      {/* xmg-sec-mfa: the one step-up prompt every sensitive action's `withStepUp` retry goes through. */}
      <StepUpDialog />
      <LegalFooter />
    </>
  );
}

function NotFound(): JSX.Element {
  return (
    <main className="page">
      <h1>{t("router.not_found")}</h1>
      <Link to="/">{t("router.not_found_home")}</Link>
    </main>
  );
}

/**
 * §B1.4: `/migrations/:id` owns no screen — it asks the state machine which screen owns the migration and goes
 * there. Every link in the console can therefore point at the migration rather than at a guess about its stage.
 */
function StageRedirect(): JSX.Element {
  const { migrationId } = useParams();
  const id = migrationId ?? "";
  const query = useMigration(id === "" ? null : id);
  if (query.isPending) return <Spinner />;
  if (query.isError || query.data === undefined) {
    return (
      <main className="page">
        <h1>{t("router.not_found")}</h1>
        <Banner tone="warn">{t("router.not_found")}</Banner>
        <Link to="/">{t("router.not_found_home")}</Link>
      </main>
    );
  }
  return <Navigate to={routeForState(id, query.data.state as MigrationState)} replace />;
}

export const routes: RouteObject[] = [
  // P1a §7: `/login` is the pre-existing one-field operator key screen, kept as the deployment-wide fallback
  // (§6). `/auth/*` are the new multiuser email/password screens; all five are anonymous (task file §2), same
  // as `/login` — none of them sits inside `RequireSession` below.
  { path: "/login", element: <LoginScreen /> },
  { path: "/auth/login", element: <AuthLoginScreen /> },
  { path: "/auth/register", element: <RegisterScreen /> },
  { path: "/auth/forgot-password", element: <ForgotPasswordScreen /> },
  { path: "/auth/reset-password", element: <ResetPasswordScreen /> },
  { path: "/auth/verify-email", element: <VerifyEmailScreen /> },
  // P5-web §3: anonymous, same reasoning as the five routes above it — the token IS the authentication
  // (`AcceptInvitationScreen`'s own docstring). It sits outside `screens/practice/routes.tsx` on purpose: that
  // module's routes are glob-mounted under `RequireSession` below, and this one may not be.
  { path: "/auth/accept-invite", element: <AcceptInvitationScreen /> },
  // xmg-sec-mfa (S3a): the second half of a sign-in. Outside `RequireSession`, which sends a password-only session
  // HERE (it would loop otherwise); each screen guards itself (anonymous -> sign-in, verified -> on).
  { path: "/auth/mfa", element: <MfaChallengeScreen /> },
  { path: "/auth/mfa/setup", element: <MfaSetupScreen /> },
  // xmg-sec-opsdocs (P1): the privacy policy and terms of use are anonymous — readable before sign-up, and
  // linked from the footer of every screen (Xero developer platform T&Cs clause 9).
  { path: "/privacy", element: <LegalScreen document="privacy" /> },
  { path: "/terms", element: <LegalScreen document="terms" /> },
  {
    element: <RequireSession />,
    children: [
      {
        element: <RootLayout />,
        children: [
          { path: "/", element: <MigrationsList /> },
          { path: "/connections", element: <ConnectionsScreen /> },
          { path: "/settings", element: <SettingsScreen /> },
          { path: "/account/security", element: <SecurityScreen /> },
          { path: "/migrations/:migrationId", element: <StageRedirect /> },
          // XeroOrg.html draws the FULL top bar on the OAuth return pages (Connections highlighted in the
          // reference) — an earlier comment claimed they were chrome-less, the pixel reference says
          // otherwise, so they mount under RootLayout like every other authenticated screen.
          { path: "/oauth/xero/return", element: <XeroReturn /> },
          { path: "/oauth/hmrc/return", element: <HmrcReturn /> },
          ...featureRoutes,
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
];
